'use strict';

document.addEventListener( 'alpine:init', () => {

	Alpine.data( 'siteAlertBar', ( config = {} ) => ({

		isHidden: false,
		isDismissed: false,
		lastScrollY: 0,
		measuredHeight: 0,
		desktopQuery: null,
		resizeObserver: null,
		dismissible: Boolean( config.dismissible ),
		dismissSeconds: Number.isFinite( Number( config.dismissSeconds ) )
			? Number( config.dismissSeconds )
			: 86400,
		fingerprint: typeof config.fingerprint === 'string' ? config.fingerprint : '',
		cookieName: typeof config.cookieName === 'string' && config.cookieName
			? config.cookieName
			: 'avinc_alert_bar_dismiss',

		init() {
			this.desktopQuery = window.matchMedia( '(min-width: 62rem)' );
			this.lastScrollY = window.scrollY || 0;

			this.onScroll = this.onScroll.bind( this );
			this.onDesktopChange = this.onDesktopChange.bind( this );

			if ( this.dismissible && this.hasActiveDismissCookie() ) {
				this.isDismissed = true;
			}

			this.publishTopOffset();
			this.measureAndPublish();
			this.placeHeader();

			if ( ! this.isDismissed ) {
				this.resizeObserver = new ResizeObserver( () => this.measureAndPublish() );
				this.resizeObserver.observe( this.$el );

				window.addEventListener( 'scroll', this.onScroll, { passive: true } );
				this.desktopQuery.addEventListener( 'change', this.onDesktopChange );
			}

			this.$watch( 'isHidden', () => this.publishHeight() );
			this.$watch( 'isDismissed', () => this.publishHeight() );
		},

		destroy() {
			window.removeEventListener( 'scroll', this.onScroll );

			if ( this.desktopQuery ) {
				this.desktopQuery.removeEventListener( 'change', this.onDesktopChange );
			}

			if ( this.resizeObserver ) {
				this.resizeObserver.disconnect();
			}

			document.documentElement.style.removeProperty( '--alert-bar-height' );
			document.body.style.removeProperty( '--alert-bar-height' );
			document.documentElement.style.removeProperty( '--alert-bar-top' );
		},

		isDesktop() {
			return this.desktopQuery && this.desktopQuery.matches;
		},

		getAdminBarOffset() {
			const site = document.querySelector( '.site' );

			if ( ! site ) {
				return 0;
			}

			const raw = getComputedStyle( site ).getPropertyValue( '--site-header-top' ).trim();
			const px = Number.parseFloat( raw );

			return Number.isFinite( px ) ? px : 0;
		},

		publishTopOffset() {
			document.documentElement.style.setProperty(
				'--alert-bar-top',
				`${ this.getAdminBarOffset() }px`
			);
		},

		measureAndPublish() {
			if ( this.isDismissed ) {
				this.publishHeight();
				return;
			}

			if ( ! this.isHidden || this.isDesktop() ) {
				const height = this.$el.offsetHeight;

				if ( height > 0 ) {
					this.measuredHeight = height;
				}
			}

			this.publishHeight();
		},

		publishHeight() {
			let height = 0;

			if ( ! this.isDismissed ) {
				// Desktop keeps natural height for sticky/flow layout even while menus hide the bar.
				height = ( this.isHidden && ! this.isDesktop() ) ? 0 : this.measuredHeight;
			}

			document.documentElement.style.setProperty( '--alert-bar-height', `${ height }px` );
			document.body.style.setProperty( '--alert-bar-height', `${ height }px` );
			this.placeHeader();
		},

		onDesktopChange() {
			if ( this.isDismissed ) {
				return;
			}

			if ( this.isDesktop() ) {
				this.isHidden = false;
			}

			this.publishTopOffset();
			this.$nextTick( () => this.measureAndPublish() );
		},

		placeHeader() {
			const header = document.getElementById( 'masthead' );

			if ( ! header ) {
				return;
			}

			if ( this.isDismissed || ! this.isDesktop() ) {
				header.style.top = '';
				return;
			}

			const admin = this.getAdminBarOffset();
			const top   = Math.max( admin, this.$el.getBoundingClientRect().bottom );

			header.style.top = `${ top }px`;
		},

		onScroll() {
			if ( this.isDesktop() ) {
				this.placeHeader();
			}

			if ( this.isDismissed || this.isDesktop() ) {
				if ( ! this.isDismissed && this.isHidden ) {
					this.isHidden = false;
				}
				return;
			}

			const y = window.scrollY || 0;

			if ( y <= 8 ) {
				this.isHidden = false;
			} else if ( y > this.lastScrollY + 6 ) {
				this.isHidden = true;
			} else if ( y < this.lastScrollY - 6 ) {
				this.isHidden = false;
			}

			this.lastScrollY = y;
		},

		getCookie( name ) {
			const prefix = `${ encodeURIComponent( name ) }=`;
			const parts = document.cookie ? document.cookie.split( ';' ) : [];

			for ( let i = 0; i < parts.length; i++ ) {
				const part = parts[ i ].trim();

				if ( part.indexOf( prefix ) === 0 ) {
					return decodeURIComponent( part.slice( prefix.length ) );
				}
			}

			return '';
		},

		hasActiveDismissCookie() {
			if ( ! this.fingerprint ) {
				return false;
			}

			return this.getCookie( this.cookieName ) === this.fingerprint;
		},

		setDismissCookie() {
			if ( ! this.fingerprint ) {
				return;
			}

			const secure = window.location.protocol === 'https:' ? '; Secure' : '';
			let cookie = `${ encodeURIComponent( this.cookieName ) }=${ encodeURIComponent( this.fingerprint ) }; Path=/; SameSite=Lax${ secure }`;

			if ( this.dismissSeconds > 0 ) {
				cookie += `; Max-Age=${ Math.floor( this.dismissSeconds ) }`;
			}

			document.cookie = cookie;
		},

		dismiss() {
			if ( ! this.dismissible || this.isDismissed ) {
				return;
			}

			this.isDismissed = true;
			this.isHidden = true;
			this.setDismissCookie();
			this.publishHeight();

			window.removeEventListener( 'scroll', this.onScroll );

			if ( this.resizeObserver ) {
				this.resizeObserver.disconnect();
				this.resizeObserver = null;
			}
		},
	}) );
});
